import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Entity, QueryOptions, Row } from 'gossamer-unified';
import {
  GCacheQuery,
  GCacheState,
  getDefaultGCacheQueryEntry,
  getQueryFromGCache,
  clearSingleQueryInGCache,
  selectQueryFromGCache,
} from './ReduxGCache';
import { SubSectionMultiProps } from './QueryMulti';

export const useCacheQuery = <T extends Row>(
  entity: Entity<T>,
  queryOptions: QueryOptions,
  skip?: boolean,
  pollInterval?: number,
): GCacheQuery<T> => {
  const queryOptionsStr = JSON.stringify(queryOptions);
  const dispatch = useDispatch();
  const cacheItem =
    useSelector(
      (state: { cache: GCacheState }) => selectQueryFromGCache(state, entity.id, queryOptionsStr) as GCacheQuery<T>,
    ) || getDefaultGCacheQueryEntry(entity.id, queryOptionsStr);
  const isExpired = cacheItem.expiresAt < Date.now();
  const needsLoad = !skip && (isExpired || (!cacheItem.data && !cacheItem.isFetching));
  useEffect(() => {
    if (needsLoad) {
      dispatch(
        getQueryFromGCache({
          entity,
          query: queryOptions,
        }),
      );
    }
  }, [entity.id, queryOptionsStr, needsLoad]);

  // Polling: periodically invalidate the cache entry to trigger a refetch
  const entityIdRef = useRef(entity.id);
  entityIdRef.current = entity.id;
  const queryOptionsStrRef = useRef(queryOptionsStr);
  queryOptionsStrRef.current = queryOptionsStr;
  useEffect(() => {
    if (!pollInterval || skip) return;
    const interval = setInterval(() => {
      dispatch(
        clearSingleQueryInGCache({
          entityId: entityIdRef.current,
          query: queryOptionsStrRef.current,
        }),
      );
    }, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval, skip, dispatch]);

  return cacheItem;
};

export interface GCacheQuerySectionProps<T extends Row> {
  entity: Entity<T>;
  excludeValues?: string[];
  gCacheQuery: GCacheQuery<T>;
  onRowClick?: (keyValue: string) => void;
  queryOptions?: QueryOptions;
  // render?: (props: GCacheQuerySectionProps<T>) => JSX.Element;
  render?: (props: SubSectionMultiProps<T>) => React.JSX.Element;
  rowClickSetsSearchPaths?: boolean;
  setQueryOptions?: (newQueryOptions: QueryOptions) => void;
}
